/**
GlobalChatAPI.ts - Global Chat Singleton API for @mindhiveoy/mainio-agent

custom variables, and command registration for browser functions that can
be invoked by the backend AI. It allows registration of commands with
detailed argument definitions (using a JSON Schema based 'parameters' field) and
integrates with a listener mechanism so that commands can be executed with the correct function signature.
This module provides a singleton that manages multiple ChatClient instances,
*/
import { ChatControlAPI } from './ChatControlAPI';
/**
 * GlobalChatAPI is a singleton class that manages multiple ChatClient instances,
 * custom variables, and command registration for browser functions that can
 * be invoked by the backend AI. It provides type-safe methods to register chat instances,
 * set custom variables, register listeners, and trigger commands based on the registered
 * command definitions.
 */
export class GlobalChatAPI {
    /**
     * Private constructor for singleton. Commands are injected during construction.
     */
    constructor() {
        /**
         * Map of chat instances keyed by chatId
         */
        this.chatControllers = new Map();
        /**
         * Global preprocessor plugins that will be added to all chat clients
         */
        this.globalPlugins = [];
        /**
         * Get the chat controller for a specific chat instance
         *
         * @param {string} agentId                Unique identifier for the agent in mainio platform
         * @returns {ChatControlAPI | undefined}  ChatControlAPI instance for the specified chat,
         *                                        if not found returns undefined
         */
        this.getChatControl = (agentId) => {
            let control = this.chatControllers.get(agentId);
            if (control) {
                return control;
            }
            control = new ChatControlAPI(this, agentId, {});
            this.chatControllers.set(agentId, control);
            return control;
        };
    }
    /**
     * Retrieves the global instance of GlobalChatAPI from globalThis, if it exists.
     * This is used to ensure that only one instance of GlobalChatAPI exists globally,
     * especially in scenarios where the package is compiled as embedded code.
     *
     * The use of a global instance allows the same instance to be shared across different
     * parts of the application that might be running in separate modules or contexts.
     *
     * @returns {GlobalChatAPI | undefined} The global instance if it exists, otherwise undefined.
     */
    static retrieveGlobalInstance() {
        return globalThis.__GLOBAL_CHAT_API_INSTANCE__;
    }
    /**
     * Stores the given instance into globalThis, making it available as a global singleton.
     * This is used to ensure that the same GlobalChatAPI instance is accessible across different modules.
     *
     * @param instance - The GlobalChatAPI instance to store.
     */
    static storeGlobalInstance(instance) {
        globalThis.__GLOBAL_CHAT_API_INSTANCE__ = instance;
    }
    /**
     * Returns the singleton instance of GlobalChatAPI. The first call must provide
     * the command registry.
     *
     * @returns {GlobalChatAPI} The GlobalChatAPI instance.
     */
    static getInstance() {
        const globalInstance = GlobalChatAPI.retrieveGlobalInstance();
        if (globalInstance) {
            return globalInstance;
        }
        if (!GlobalChatAPI.instance) {
            GlobalChatAPI.instance = new GlobalChatAPI();
            GlobalChatAPI.storeGlobalInstance(GlobalChatAPI.instance);
        }
        return GlobalChatAPI.instance;
    }
    /**
     * Add a preprocessor plugin that will be automatically added to all chat clients.
     * This affects both existing and future chat clients.
     *
     * @param plugin The preprocessor plugin to add globally
     */
    addGlobalPreprocessorPlugin(plugin) {
        this.globalPlugins.push(plugin);
        // Add to all existing chat clients
        for (const controller of Array.from(this.chatControllers.values())) {
            controller.addPreprocessorPlugin(plugin);
        }
    }
    /**
     * Register a chat controller for a specific chat instance and bind it to the chat client
     *
     * @param agentId - Unique identifier for the agent in mainio platform
     * @param chatClient - Instance of ChatClient
     * @returns {ChatControlAPI} - The registered chat controller
     */
    registerChat(agentId, chatClient) {
        // Add all global plugins to the new chat client
        for (const plugin of this.globalPlugins) {
            chatClient.addPreprocessorPlugin(plugin);
        }
        const chatController = new ChatControlAPI(this, agentId, chatClient);
        this.chatControllers.set(agentId, chatController);
        return chatController;
    }
}
export const globalChatAPI = GlobalChatAPI.getInstance();
//# sourceMappingURL=global-instance.js.map